// src/pages/Settings.js — Integrations & account settings
import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../lib/api'
import { useIsPreview } from '../lib/preview'
import { useRoleOptions } from '../lib/roles'
import { useAuth } from '../context/AuthContext'
import PasswordForm from '../components/PasswordForm'

// ── Helpers ───────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  })
}

// Same lists as every other form (lib/roles.js). This one used to offer
// its own ("LP / Investor", "GP / Fund Manager", "Prospect", "Other") and the
// Gmail create-contact endpoint doesn't validate, which is how those values
// got onto contacts.

// Try to pull a company name out of an email display name.
// Recognises common separators: " - ", " | ", " @ ", and "(...)".
// Returns '' when the display name looks like just a person's name.
function extractCompanyName(displayName) {
  if (!displayName) return ''
  const dn = String(displayName).trim()

  // "Name (Company)" → Company
  const paren = dn.match(/\(([^)]+)\)\s*$/)
  if (paren) return paren[1].trim()

  // "Name - Company" / "Name | Company" / "Name @ Company" → Company
  const sep = dn.match(/\s+[-|@]\s+(.+)$/)
  if (sep) return sep[1].trim()

  // No company signal — just a personal name
  return ''
}

// ── Gmail section ─────────────────────────────────────────────────────────

function GmailSection() {
  const qc = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const [syncResult, setSyncResult] = useState(null)
  const [banner, setBanner] = useState(null)   // { type: 'success'|'error', msg }

  // Pick up ?gmail=connected or ?gmail=error from OAuth callback redirect
  useEffect(() => {
    const status = searchParams.get('gmail')
    const reason = searchParams.get('reason')
    if (status === 'connected') {
      setBanner({ type: 'success', msg: 'Gmail connected successfully.' })
      qc.invalidateQueries({ queryKey: ['gmailStatus'] })
    } else if (status === 'error') {
      setBanner({ type: 'error', msg: `Connection failed: ${reason || 'unknown error'}` })
    }
    // Clean query params from URL without re-rendering
    if (status) {
      setSearchParams({}, { replace: true })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const { data: statusData, isLoading: statusLoading } = useQuery({
    queryKey: ['gmailStatus'],
    queryFn: () => api.get('/api/gmail/status').then(r => r.data.data),
    staleTime: 30 * 1000,
  })

  const connectMutation = useMutation({
    mutationFn: () => api.get('/api/auth/google').then(r => r.data.data),
    onSuccess: ({ url }) => {
      window.location.href = url
    },
    onError: (err) => {
      setBanner({ type: 'error', msg: err.response?.data?.error || err.message })
    },
  })

  const syncMutation = useMutation({
    mutationFn: () => api.post('/api/gmail/sync', {}, { params: { limit: 50 } }).then(r => r.data.data),
    onSuccess: (data) => {
      setSyncResult(data)
      qc.invalidateQueries({ queryKey: ['gmailStatus'] })
      qc.invalidateQueries({ queryKey: ['interactions', 'all'] })
    },
    onError: (err) => {
      setBanner({ type: 'error', msg: err.response?.data?.error || err.message })
    },
  })

  const connected = statusData?.connected ?? false

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        {/* Gmail logo-ish icon */}
        <div style={{
          width: 36, height: 36, borderRadius: 8,
          background: 'linear-gradient(135deg, #ea4335 0%, #fbbc05 50%, #34a853 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <svg width="18" height="14" viewBox="0 0 18 14" fill="none">
            <path d="M0 2C0 .9.9 0 2 0h14c1.1 0 2 .9 2 2v10c0 1.1-.9 2-2 2H2c-1.1 0-2-.9-2-2V2z" fill="white" fillOpacity=".15"/>
            <path d="M0 2l9 6 9-6" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </div>
        <div>
          <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 2 }}>Gmail</h2>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            Sync emails to Interactions and auto-match contacts by email address.
          </p>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          {statusLoading ? (
            <div className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
          ) : (
            <span className={`badge ${connected ? 'badge-green' : 'badge-gray'}`}>
              {connected ? 'Connected' : 'Not connected'}
            </span>
          )}
        </div>
      </div>

      {/* Banner */}
      {banner && (
        <div style={{
          background: banner.type === 'success' ? 'rgba(16,185,129,0.1)' : 'var(--danger-muted)',
          border: `1px solid ${banner.type === 'success' ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.25)'}`,
          borderRadius: 6, padding: '10px 14px', fontSize: 13,
          color: banner.type === 'success' ? 'var(--success-text)' : 'var(--danger-text)',
          marginBottom: 16,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span>{banner.msg}</span>
          <button onClick={() => setBanner(null)} style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'inherit', opacity: 0.7, fontSize: 16, lineHeight: 1, padding: '0 4px',
          }}>✕</button>
        </div>
      )}

      {/* Not connected state */}
      {!statusLoading && !connected && (
        <div style={{
          background: 'var(--surface-raised)', border: '1px solid var(--border)',
          borderRadius: 8, padding: '24px', textAlign: 'center',
        }}>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
            Connect your Google account to automatically sync emails as Interactions
            and match them to existing contacts.
          </p>
          <button
            className="btn btn-primary"
            onClick={() => connectMutation.mutate()}
            disabled={connectMutation.isPending}
          >
            {connectMutation.isPending ? 'Redirecting…' : 'Connect Gmail'}
          </button>
          <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 12 }}>
            Read-only access · No emails are sent on your behalf
          </p>
        </div>
      )}

      {/* Connected state */}
      {!statusLoading && connected && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Stats row */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12,
          }}>
            {[
              { label: 'Account', value: statusData?.email || '—' },
              { label: 'Last synced', value: formatDate(statusData?.last_sync) },
              { label: 'Emails imported', value: (statusData?.email_count ?? 0).toLocaleString() },
            ].map(({ label, value }) => (
              <div key={label} style={{
                background: 'var(--surface-raised)', borderRadius: 6,
                padding: '12px 14px', border: '1px solid var(--border)',
              }}>
                <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
                  letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 4 }}>
                  {label}
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-primary)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {value}
                </div>
              </div>
            ))}
          </div>

          {/* Sync result */}
          {syncResult && (
            <div style={{
              background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)',
              borderRadius: 6, padding: '12px 16px', fontSize: 13,
              display: 'flex', gap: 20, flexWrap: 'wrap',
            }}>
              <span style={{ color: 'var(--success-text)', fontWeight: 600 }}>Sync complete</span>
              <span style={{ color: 'var(--text-secondary)' }}>
                <strong style={{ color: 'var(--text-primary)' }}>{syncResult.synced}</strong> imported
              </span>
              <span style={{ color: 'var(--text-secondary)' }}>
                <strong style={{ color: 'var(--text-primary)' }}>{syncResult.matched}</strong> matched to contacts
              </span>
              <span style={{ color: 'var(--text-secondary)' }}>
                <strong style={{ color: 'var(--text-primary)' }}>{syncResult.unmatched}</strong> unmatched
              </span>
              {syncResult.hasMore && (
                <span style={{ color: 'var(--warning)', fontWeight: 500 }}>
                  · More emails available — sync again to continue
                </span>
              )}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button
              className="btn btn-primary"
              onClick={() => { setSyncResult(null); syncMutation.mutate() }}
              disabled={syncMutation.isPending}
            >
              {syncMutation.isPending ? (
                <>
                  <span className="spinner" style={{ width: 13, height: 13, borderWidth: 2 }} />
                  Syncing…
                </>
              ) : 'Sync Now'}
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => connectMutation.mutate()}
              disabled={connectMutation.isPending}
            >
              Reconnect
            </button>
            {syncMutation.isPending && (
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                This may take a moment…
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Calendar section ──────────────────────────────────────────────────────

function CalendarSection() {
  const qc = useQueryClient()
  const [syncResult, setSyncResult] = useState(null)
  const [banner, setBanner] = useState(null)

  const { data: statusData, isLoading: statusLoading } = useQuery({
    queryKey: ['calendarStatus'],
    queryFn: () => api.get('/api/calendar/status').then(r => r.data.data),
    staleTime: 30 * 1000,
  })

  const syncMutation = useMutation({
    mutationFn: () => api.post('/api/calendar/sync').then(r => r.data.data),
    onSuccess: (data) => {
      setSyncResult(data)
      qc.invalidateQueries({ queryKey: ['calendarStatus'] })
      qc.invalidateQueries({ queryKey: ['interactions', 'all'] })
    },
    onError: (err) => {
      setBanner({ type: 'error', msg: err.response?.data?.error || err.message })
    },
  })

  const connected = statusData?.connected ?? false

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 8,
          background: 'linear-gradient(135deg, #4285F4 0%, #34A853 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          {/* Calendar icon */}
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <rect x="1" y="3" width="16" height="14" rx="2" stroke="white" strokeWidth="1.5"/>
            <path d="M1 7h16" stroke="white" strokeWidth="1.5"/>
            <path d="M5 1v4M13 1v4" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
            <rect x="4" y="10" width="3" height="2" rx=".5" fill="white"/>
            <rect x="8" y="10" width="3" height="2" rx=".5" fill="white"/>
          </svg>
        </div>
        <div>
          <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 2 }}>Google Calendar</h2>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            Sync calendar events to Interactions and auto-match attendees to contacts.
          </p>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          {statusLoading ? (
            <div className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
          ) : (
            <span className={`badge ${connected ? 'badge-green' : 'badge-gray'}`}>
              {connected ? 'Connected' : 'Not connected'}
            </span>
          )}
        </div>
      </div>

      {/* Banner */}
      {banner && (
        <div style={{
          background: 'var(--danger-muted)',
          border: '1px solid rgba(239,68,68,0.25)',
          borderRadius: 6, padding: '10px 14px', fontSize: 13,
          color: 'var(--danger-text)', marginBottom: 16,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span>{banner.msg}</span>
          <button onClick={() => setBanner(null)} style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'inherit', opacity: 0.7, fontSize: 16, lineHeight: 1, padding: '0 4px',
          }}>✕</button>
        </div>
      )}

      {/* Not connected */}
      {!statusLoading && !connected && (
        <div style={{
          background: 'var(--surface-raised)', border: '1px solid var(--border)',
          borderRadius: 8, padding: '24px', textAlign: 'center',
        }}>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>
            Calendar access is included with your Gmail connection.
          </p>
          <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            Connect Gmail above to enable calendar sync.
          </p>
        </div>
      )}

      {/* Connected */}
      {!statusLoading && connected && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            {[
              { label: 'Account',           value: statusData?.email || '—' },
              { label: 'Last synced',        value: formatDate(statusData?.last_sync) },
              { label: 'Events imported',    value: (statusData?.event_count ?? 0).toLocaleString() },
            ].map(({ label, value }) => (
              <div key={label} style={{
                background: 'var(--surface-raised)', borderRadius: 6,
                padding: '12px 14px', border: '1px solid var(--border)',
              }}>
                <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
                  letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 4 }}>
                  {label}
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-primary)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {value}
                </div>
              </div>
            ))}
          </div>

          {syncResult && (
            <div style={{
              background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)',
              borderRadius: 6, padding: '12px 16px', fontSize: 13,
              display: 'flex', gap: 20, flexWrap: 'wrap',
            }}>
              <span style={{ color: 'var(--success-text)', fontWeight: 600 }}>Sync complete</span>
              <span style={{ color: 'var(--text-secondary)' }}>
                <strong style={{ color: 'var(--text-primary)' }}>{syncResult.synced}</strong> imported
              </span>
              <span style={{ color: 'var(--text-secondary)' }}>
                <strong style={{ color: 'var(--text-primary)' }}>{syncResult.matched}</strong> matched to contacts
              </span>
              <span style={{ color: 'var(--text-secondary)' }}>
                <strong style={{ color: 'var(--text-primary)' }}>{syncResult.skipped}</strong> already imported
              </span>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button
              className="btn btn-primary"
              onClick={() => { setSyncResult(null); syncMutation.mutate() }}
              disabled={syncMutation.isPending}
            >
              {syncMutation.isPending ? (
                <>
                  <span className="spinner" style={{ width: 13, height: 13, borderWidth: 2 }} />
                  Syncing…
                </>
              ) : 'Sync Now'}
            </button>
            {syncMutation.isPending && (
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                This may take a moment…
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Mandate ↔ Gmail Label Mappings Section ────────────────────────────────
// Drives the auto-tagging in gmailController.js: a message carrying a
// mapped label (or a "/"-child of one) gets its mandate_id set on sync.

function MandateLabelMappingsSection() {
  const qc = useQueryClient()
  const [selectedMandateId, setSelectedMandateId] = useState('')
  const [selectedLabel, setSelectedLabel]         = useState('')
  const [err, setErr] = useState(null)

  const { data: mappingsData, isLoading: mappingsLoading } = useQuery({
    queryKey: ['mandateLabelMappings'],
    queryFn: () => api.get('/api/mandate-label-mappings').then(r => r.data.data ?? []),
  })

  const { data: mandatesData } = useQuery({
    queryKey: ['mandates'],
    queryFn: () => api.get('/api/mandates').then(r => r.data.data ?? []),
  })

  const { data: labelsData, isLoading: labelsLoading, error: labelsError } = useQuery({
    queryKey: ['gmailLabels'],
    queryFn: () => api.get('/api/gmail/labels').then(r => r.data.data ?? []),
  })

  const mappings = mappingsData ?? []
  const mandates = mandatesData ?? []
  const allLabels = labelsData ?? []

  // Labels already mapped are hidden from the "add" picker — the label
  // column has a unique constraint, so re-offering them would just 409.
  const mappedLabelNames = new Set(mappings.map(m => m.label_name))
  const availableLabels  = allLabels.filter(l => !mappedLabelNames.has(l))

  const addMutation = useMutation({
    mutationFn: ({ mandate_id, label_name }) =>
      api.post('/api/mandate-label-mappings', { mandate_id, label_name }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mandateLabelMappings'] })
      setSelectedMandateId('')
      setSelectedLabel('')
      setErr(null)
    },
    onError: (e) => setErr(e.response?.data?.error || e.message),
  })

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/api/mandate-label-mappings/${id}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mandateLabelMappings'] }),
  })

  function handleAdd() {
    if (!selectedMandateId || !selectedLabel) {
      return setErr('Choose both a mandate and a label.')
    }
    addMutation.mutate({ mandate_id: selectedMandateId, label_name: selectedLabel })
  }

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 2 }}>Mandate ↔ Gmail Label Mappings</h2>
        <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          Emails carrying a mapped label (or any of its sub-labels) are automatically tagged
          with the matching mandate when synced.
        </p>
      </div>

      {err && (
        <div style={{
          background: 'var(--danger-muted)', border: '1px solid rgba(239,68,68,0.25)',
          borderRadius: 6, padding: '10px 14px', fontSize: 13, color: 'var(--danger-text)', marginBottom: 12,
        }}>{err}</div>
      )}

      {mappingsLoading ? (
        <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Loading mappings…</p>
      ) : (
        <div className="table-wrapper" style={{ marginBottom: 16 }}>
          <table>
            <thead>
              <tr>
                <th>Gmail Label</th>
                <th>Mandate</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {mappings.map(row => (
                <tr key={row.id}>
                  <td><code style={{ fontSize: 12 }}>{row.label_name}</code></td>
                  <td>{row.mandate?.mandate_name || '—'}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-ghost"
                      disabled={deleteMutation.isPending && deleteMutation.variables === row.id}
                      onClick={() => deleteMutation.mutate(row.id)}
                    >
                      {deleteMutation.isPending && deleteMutation.variables === row.id ? 'Removing…' : 'Remove'}
                    </button>
                  </td>
                </tr>
              ))}
              {mappings.length === 0 && (
                <tr><td colSpan={3} style={{ color: 'var(--text-secondary)' }}>No mappings yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add mapping */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div className="input-group" style={{ minWidth: 200 }}>
          <label className="input-label">Mandate</label>
          <select
            className="select"
            value={selectedMandateId}
            onChange={e => { setSelectedMandateId(e.target.value); setErr(null) }}
          >
            <option value="">Select mandate…</option>
            {mandates.map(m => (
              <option key={m.mandate_id} value={m.mandate_id}>{m.mandate_name}</option>
            ))}
          </select>
        </div>

        <div className="input-group" style={{ minWidth: 220 }}>
          <label className="input-label">Gmail label</label>
          <select
            className="select"
            value={selectedLabel}
            onChange={e => { setSelectedLabel(e.target.value); setErr(null) }}
            disabled={labelsLoading}
          >
            <option value="">
              {labelsLoading ? 'Loading labels…' : 'Select label…'}
            </option>
            {availableLabels.map(l => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
          {labelsError && (
            <div style={{ fontSize: 12, color: 'var(--danger-text)', marginTop: 4 }}>
              Could not load Gmail labels — connect Gmail above first.
            </div>
          )}
        </div>

        <button
          className="btn btn-primary"
          onClick={handleAdd}
          disabled={addMutation.isPending}
        >
          {addMutation.isPending ? 'Adding…' : 'Add mapping'}
        </button>
      </div>
    </div>
  )
}

// ── Create Contact Modal (from unmatched email) ───────────────────────────

function CreateContactModal({ record, onClose, onCreated }) {
  const { contactTypes, firmTypes } = useRoleOptions()
  const [form, setForm] = useState({
    name:         record.display_name || record.email_address.split('@')[0],
    contact_type: [],
    title_role:   '',
  })
  // Firm section state
  const [firmName, setFirmName]           = useState(() => extractCompanyName(record.display_name))
  const [selectedFirmId, setSelectedFirmId] = useState('')  // set when an existing firm is chosen
  const [firmType, setFirmType]           = useState('')
  const [showFirmMenu, setShowFirmMenu]   = useState(false)

  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState(null)

  const { data: firmsData = [] } = useQuery({
    queryKey: ['firms'],
    queryFn: () => api.get('/api/firms').then(r => r.data.data ?? []),
  })

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); setErr(null) }

  function toggleType(t) {
    set('contact_type', form.contact_type.includes(t)
      ? form.contact_type.filter(x => x !== t)
      : [...form.contact_type, t])
  }

  // Typeahead matches against existing firms (client-side, case-insensitive)
  const firmMatches = (() => {
    const q = firmName.trim().toLowerCase()
    if (!q) return []
    return firmsData
      .filter(f => f.name.toLowerCase().includes(q))
      .slice(0, 8)
  })()

  // An existing firm is "selected" only if the id is set AND the typed name
  // still exactly matches that firm (editing the text re-opens create mode).
  const selectedFirm = selectedFirmId
    ? firmsData.find(f => f.firm_id === selectedFirmId)
    : null
  const isExistingSelected = Boolean(
    selectedFirm && selectedFirm.name.toLowerCase() === firmName.trim().toLowerCase()
  )
  const willCreateFirm = firmName.trim().length > 0 && !isExistingSelected

  function onFirmNameChange(v) {
    setFirmName(v)
    setSelectedFirmId('')      // typing invalidates a prior selection
    setShowFirmMenu(true)
    setErr(null)
  }

  function chooseFirm(f) {
    setFirmName(f.name)
    setSelectedFirmId(f.firm_id)
    setShowFirmMenu(false)
    setErr(null)
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.name.trim()) return setErr('Name is required.')
    if (willCreateFirm && !firmType) {
      return setErr('Firm Type is required when creating a new firm.')
    }
    setSubmitting(true); setErr(null)
    try {
      const body = {
        name:         form.name.trim(),
        contact_type: form.contact_type.length ? form.contact_type : null,
        title_role:   form.title_role || null,
      }
      if (isExistingSelected) {
        body.firm_id = selectedFirmId
      } else if (willCreateFirm) {
        body.firm_name = firmName.trim()
        body.firm_type = [firmType]
      }
      const res = await api.post(`/api/gmail/unmatched/${record.id}/create-contact`, body)
      onCreated(res.data.data)
    } catch (e) {
      setErr(e.response?.data?.error || e.message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      overflowY: 'auto', zIndex: 1000, padding: '2rem 1rem',
    }}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border-light)',
        borderRadius: 10, width: '100%', maxWidth: 480, margin: 'auto',
        maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
      }}>
        <div style={{
          padding: '16px 24px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <h2 style={{ fontSize: 15, fontWeight: 600 }}>Create Contact</h2>
          <button onClick={onClose} style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'var(--text-secondary)', fontSize: 16,
          }}>✕</button>
        </div>

        <form onSubmit={handleSubmit} style={{
          padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14,
        }}>
          {err && (
            <div style={{
              background: 'var(--danger-muted)', border: '1px solid rgba(239,68,68,0.25)',
              borderRadius: 6, padding: '10px 14px', fontSize: 13, color: 'var(--danger-text)',
            }}>{err}</div>
          )}

          {/* Email — read-only */}
          <div className="input-group">
            <label className="input-label">Email</label>
            <input className="input" value={record.email_address} readOnly
              style={{ opacity: 0.7, cursor: 'default' }} />
          </div>

          {/* Name — editable, pre-filled */}
          <div className="input-group">
            <label className="input-label">Name *</label>
            <input className="input" value={form.name}
              onChange={e => set('name', e.target.value)}
              placeholder="Full name" />
          </div>

          {/* Title/Role */}
          <div className="input-group">
            <label className="input-label">Title / Role</label>
            <input className="input" value={form.title_role}
              onChange={e => set('title_role', e.target.value)}
              placeholder="e.g. Managing Director" />
          </div>

          {/* Firm — typeahead that matches existing or creates new */}
          <div className="input-group" style={{ position: 'relative' }}>
            <label className="input-label">Firm</label>
            <input
              className="input"
              value={firmName}
              onChange={e => onFirmNameChange(e.target.value)}
              onFocus={() => setShowFirmMenu(true)}
              onBlur={() => setTimeout(() => setShowFirmMenu(false), 150)}
              placeholder="Type a firm name…"
              autoComplete="off"
            />

            {/* Typeahead menu */}
            {showFirmMenu && firmMatches.length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
                background: 'var(--surface)', border: '1px solid var(--border-light)',
                borderRadius: 6, marginTop: 2, maxHeight: 200, overflowY: 'auto',
                boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
              }}>
                {firmMatches.map(f => (
                  <div
                    key={f.firm_id}
                    onMouseDown={() => chooseFirm(f)}   // mouseDown beats input blur
                    style={{
                      padding: '8px 12px', fontSize: 13, cursor: 'pointer',
                      color: 'var(--text-primary)',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-muted)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    {f.name}
                  </div>
                ))}
              </div>
            )}

            {/* Status indicator */}
            {isExistingSelected && (
              <div style={{ fontSize: 12, color: 'var(--success-bright)', marginTop: 4 }}>
                ✓ Existing firm
              </div>
            )}
            {willCreateFirm && (
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                Will create new firm
              </div>
            )}
          </div>

          {/* Firm Type — only when creating a new firm */}
          {willCreateFirm && (
            <div className="input-group">
              <label className="input-label">Firm Type (required for new firm)</label>
              <select className="select" value={firmType}
                onChange={e => { setFirmType(e.target.value); setErr(null) }}>
                <option value="">Select type…</option>
                {firmTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          )}

          {/* Contact Type — multi-select chips */}
          <div className="input-group">
            <label className="input-label">Contact Type</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
              {contactTypes.map(t => {
                const sel = form.contact_type.includes(t)
                return (
                  <button key={t} type="button" onClick={() => toggleType(t)}
                    style={{
                      padding: '4px 10px', borderRadius: 20, fontSize: 12,
                      border: `1px solid ${sel ? 'var(--accent)' : 'var(--border-light)'}`,
                      background: sel ? 'var(--accent-muted)' : 'transparent',
                      color: sel ? 'var(--blue-text)' : 'var(--text-secondary)',
                      cursor: 'pointer', fontFamily: 'inherit',
                    }}>
                    {t}
                  </button>
                )
              })}
            </div>
          </div>

          <div style={{
            display: 'flex', justifyContent: 'flex-end', gap: 10,
            paddingTop: 4, borderTop: '1px solid var(--border)', marginTop: 4,
          }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}
              disabled={submitting}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create Contact'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Unmatched Emails Section ──────────────────────────────────────────────

function UnmatchedEmailsSection() {
  const qc = useQueryClient()
  const [createRecord, setCreateRecord] = useState(null)  // row → opens CreateContactModal
  const [ignoringId, setIgnoringId]     = useState(null)  // row id being ignored

  const { data, isLoading, error } = useQuery({
    queryKey: ['unmatched-emails'],
    queryFn: async () => {
      const res = await api.get('/api/gmail/unmatched')
      return res.data.data || []
    }
  })

  const ignoreMutation = useMutation({
    mutationFn: (id) => api.post(`/api/gmail/unmatched/${id}/ignore`).then(r => r.data),
    onMutate: (id) => setIgnoringId(id),
    onSettled: () => {
      setIgnoringId(null)
      qc.invalidateQueries({ queryKey: ['unmatched-emails'] })
    },
  })

  if (isLoading) return <div className="card" style={{marginTop:'24px'}}><p>Loading unmatched emails...</p></div>

  if (error) return <div className="card" style={{marginTop:'24px'}}><p style={{color:'red'}}>Error loading unmatched emails: {error.message}</p></div>

  const rows = data || []

  return (
    <div className="card" style={{marginTop: '24px'}}>
      <h3>Unmatched Emails ({rows.length})</h3>
      {rows.length === 0 ? (
        <p style={{color: 'var(--text-secondary)'}}>No unmatched email addresses</p>
      ) : (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Email Address</th>
                <th>Display Name</th>
                <th>Subject</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.id}>
                  <td>{row.email_address}</td>
                  <td>{row.display_name || '—'}</td>
                  <td>{row.subject ? row.subject.substring(0, 60) : '—'}</td>
                  <td>{row.date ? new Date(row.date).toLocaleDateString() : '—'}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => setCreateRecord(row)}
                    >
                      Create Contact
                    </button>
                    <button
                      className="btn btn-sm btn-ghost"
                      style={{marginLeft: '8px'}}
                      disabled={ignoringId === row.id}
                      onClick={() => ignoreMutation.mutate(row.id)}
                    >
                      {ignoringId === row.id ? 'Ignoring…' : 'Ignore'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Contact modal — opened from the row's "Create Contact" button */}
      {createRecord && (
        <CreateContactModal
          record={createRecord}
          onClose={() => setCreateRecord(null)}
          onCreated={() => {
            setCreateRecord(null)
            qc.invalidateQueries({ queryKey: ['unmatched-emails'] })
            qc.invalidateQueries({ queryKey: ['contacts'] })
          }}
        />
      )}
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────

// ── Account: change your own password ─────────────────────────────────────
function AccountSection() {
  const { user, profile } = useAuth()
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 2 }}>Change password</h2>
      <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 16 }}>
        Signed in as {profile?.name ? `${profile.name} (${user?.email})` : user?.email}.
      </p>
      <PasswordForm />
    </div>
  )
}

// ── Team & Access (rga_admin only) ────────────────────────────────────────
// Invite people and send password-reset emails without the Supabase
// dashboard. The API enforces rga_admin; hiding the section is cosmetic.
const ROLE_LABELS = { rga_admin: 'Admin', rga_user: 'RGA staff', partner: 'Partner' }

function loginStatus(u) {
  if (!u.has_login) return 'No login'
  if (!u.password_set_at) return u.last_sign_in_at ? 'Password not set' : 'Invited, not set up'
  return u.last_sign_in_at
    ? `Last sign-in ${new Date(u.last_sign_in_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
    : 'Active'
}

function TeamAccessSection() {
  const qc = useQueryClient()
  const [form, setForm] = useState({ name: '', email: '', role: 'rga_user' })
  const [flash, setFlash] = useState(null)   // { kind: 'ok' | 'err', text }
  const [confirmResetFor, setConfirmResetFor] = useState(null)
  const [confirmDeactivate, setConfirmDeactivate] = useState(null)   // user id
  const { profile } = useAuth()

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['auth', 'users-status'],
    queryFn: () => api.get('/api/auth/users-status').then(r => r.data.data ?? []),
  })

  function show(kind, text) { setFlash({ kind, text }); setTimeout(() => setFlash(null), 6000) }
  const errText = e => e.response?.data?.error || e.message

  const invite = useMutation({
    mutationFn: body => api.post('/api/auth/invite', body),
    onSuccess: (_r, body) => {
      show('ok', `Invite sent to ${body.email}. The link takes them to "Set your password".`)
      setForm({ name: '', email: '', role: 'rga_user' })
      qc.invalidateQueries({ queryKey: ['auth', 'users-status'] })
      qc.invalidateQueries({ queryKey: ['users'] })
    },
    onError: e => show('err', errText(e)),
  })

  const sendReset = useMutation({
    mutationFn: email => api.post('/api/auth/send-reset', { email }),
    onSuccess: (_r, email) => { setConfirmResetFor(null); show('ok', `Reset email sent to ${email}.`) },
    onError: e => { setConfirmResetFor(null); show('err', errText(e)) },
  })

  // Deactivate keeps every record; it blocks sign-in and marks their contact
  // "Former RGA Team" (see authAccountController.setActive).
  const setActive = useMutation({
    mutationFn: ({ u, active }) => api.post(`/api/auth/users/${u.id}/${active ? 'reactivate' : 'deactivate'}`),
    onSuccess: (_r, { u, active }) => {
      setConfirmDeactivate(null)
      show('ok', active ? `${u.name} reactivated.` : `${u.name} deactivated. Their records and history are kept.`)
      qc.invalidateQueries({ queryKey: ['auth', 'users-status'] })
      qc.invalidateQueries({ queryKey: ['users'] })
      qc.invalidateQueries({ queryKey: ['relationship-owners'] })
      qc.invalidateQueries({ queryKey: ['contacts'] })
    },
    onError: e => { setConfirmDeactivate(null); show('err', errText(e)) },
  })
  const isStaff = u => u.role === 'rga_admin' || u.role === 'rga_user'

  function submitInvite(e) {
    e.preventDefault()
    if (!form.name.trim() || !form.email.trim()) return show('err', 'Name and email are required.')
    invite.mutate({ name: form.name.trim(), email: form.email.trim(), role: form.role })
  }

  const linkBtn = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, color: 'var(--accent)' }

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 2 }}>Team &amp; Access</h2>
      <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 16 }}>
        Invite people, send password-reset emails, and deactivate people who have left (their records and history are kept). Admins only.
      </p>

      {flash && (
        <div role={flash.kind === 'err' ? 'alert' : 'status'} style={{
          marginBottom: 12, padding: '8px 12px', borderRadius: 6, fontSize: 12,
          background: flash.kind === 'err' ? 'var(--danger-muted)' : 'var(--success-muted)',
          color: flash.kind === 'err' ? 'var(--danger-text, #f87171)' : 'var(--success-text, #34d399)',
        }}>{flash.text}</div>
      )}

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}><div className="spinner" /></div>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 6, marginBottom: 16 }}>
          {users.map((u, i) => (
            <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 12px', flexWrap: 'wrap',
              borderBottom: i < users.length - 1 ? '1px solid var(--border)' : 'none', fontSize: 13 }}>
              <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                <div style={{ fontWeight: 500 }}>{u.name}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{u.email}</div>
              </div>
              <span className="badge badge-gray" style={{ fontSize: 10 }}>{ROLE_LABELS[u.role] ?? u.role}</span>
              {u.deactivated_at && <span className="badge badge-red" style={{ fontSize: 10 }}>Deactivated</span>}
              <span style={{ fontSize: 11, color: 'var(--text-secondary)', minWidth: 130 }}>
                {u.deactivated_at
                  ? `Since ${new Date(u.deactivated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                  : loginStatus(u)}
              </span>
              {isStaff(u) && u.email !== profile?.email && (u.deactivated_at ? (
                <button style={linkBtn} disabled={setActive.isPending}
                  onClick={() => setActive.mutate({ u, active: true })}>Reactivate</button>
              ) : confirmDeactivate === u.id ? (
                <span style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12 }}>
                  <span style={{ color: 'var(--text-secondary)' }}>Deactivate {u.name}?</span>
                  <button style={{ ...linkBtn, fontWeight: 600, color: 'var(--danger)' }} disabled={setActive.isPending}
                    onClick={() => setActive.mutate({ u, active: false })}>{setActive.isPending ? 'Working…' : 'Deactivate'}</button>
                  <button style={{ ...linkBtn, color: 'var(--text-muted)' }} onClick={() => setConfirmDeactivate(null)}>Cancel</button>
                </span>
              ) : (
                <button style={{ ...linkBtn, color: 'var(--text-muted)' }} onClick={() => setConfirmDeactivate(u.id)}>Deactivate</button>
              ))}
              {u.has_login && !u.deactivated_at && (confirmResetFor === u.email ? (
                <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button style={{ ...linkBtn, fontWeight: 600 }} disabled={sendReset.isPending}
                    onClick={() => sendReset.mutate(u.email)}>{sendReset.isPending ? 'Sending…' : 'Send'}</button>
                  <button style={{ ...linkBtn, color: 'var(--text-muted)' }} onClick={() => setConfirmResetFor(null)}>Cancel</button>
                </span>
              ) : (
                <button style={linkBtn} onClick={() => setConfirmResetFor(u.email)}>Send reset email</button>
              ))}
            </div>
          ))}
        </div>
      )}

      <form onSubmit={submitInvite} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="input-group" style={{ flex: '1 1 150px' }}>
          <label className="input-label" htmlFor="invite-name">Name</label>
          <input id="invite-name" className="input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
        </div>
        <div className="input-group" style={{ flex: '1 1 190px' }}>
          <label className="input-label" htmlFor="invite-email">Email</label>
          <input id="invite-email" className="input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
        </div>
        <div className="input-group" style={{ flex: '0 1 130px' }}>
          <label className="input-label" htmlFor="invite-role">Role</label>
          <select id="invite-role" className="select" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
            <option value="rga_user">RGA staff</option>
            <option value="rga_admin">Admin</option>
            <option value="partner">Partner</option>
          </select>
        </div>
        <button type="submit" className="btn btn-primary btn-sm" disabled={invite.isPending}>
          {invite.isPending ? 'Sending…' : 'Send invite'}
        </button>
      </form>
    </div>
  )
}

// ── Roles (admin only) ───────────────────────────────────────────────────
// The allowed contact roles and firm types (role_options). Forms only offer
// these and the database rejects anything else, so new roles start here.
// One table, a row per role per kind (contacts first, then firms), each
// tagged Contact or Firm, with its usage count. A role can be added for
// contacts, firms or both; deleted once nothing uses it; system roles (set
// automatically, e.g. Former RGA Team) can't be deleted.
function RoleKindToggle({ on, onClick, children }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={on} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 999,
      fontSize: 12, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer',
      border: `1px solid ${on ? 'var(--accent)' : 'var(--border-light)'}`,
      background: on ? 'var(--accent-muted)' : 'transparent',
      color: on ? 'var(--accent)' : 'var(--text-secondary)',
    }}>
      <span style={{ width: 12, textAlign: 'center' }}>{on ? '✓' : '+'}</span>{children}
    </button>
  )
}

function RolesSection() {
  const qc = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [name, setName] = useState('')
  const [kinds, setKinds] = useState({ contact: true, firm: false })
  const [flash, setFlash] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(null)   // `${kind}:${value}`

  const { data, isLoading } = useQuery({
    queryKey: ['role-options', 'counts'],
    queryFn: () => api.get('/api/roles', { params: { counts: 1 } }).then(r => r.data.data),
  })

  function show(kind, text) { setFlash({ kind, text }); setTimeout(() => setFlash(null), 6000) }
  const errText = e => e.response?.data?.error || e.message
  const refresh = () => qc.invalidateQueries({ queryKey: ['role-options'] })

  // Contacts first, then firms; most used first within each.
  const rows = useMemo(() => ['contact', 'firm'].flatMap(kind =>
    [...(data?.[kind] ?? [])]
      .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value))
      .map(r => ({ ...r, kind }))
  ), [data])

  const chosen = Object.keys(kinds).filter(k => kinds[k])
  const add = useMutation({
    mutationFn: () => api.post('/api/roles', { value: name.trim(), kinds: chosen }),
    onSuccess: () => {
      const where = chosen.length === 2 ? 'individuals and firms' : chosen[0] === 'contact' ? 'individuals' : 'firms'
      show('ok', `Added "${name.trim()}" for ${where}.`)
      setName(''); setKinds({ contact: true, firm: false }); setAdding(false); refresh()
    },
    onError: e => show('err', errText(e)),
  })
  const remove = useMutation({
    mutationFn: row => api.delete(`/api/roles/${row.kind}/${encodeURIComponent(row.value)}`),
    onSuccess: (_r, row) => { setConfirmDelete(null); show('ok', `Deleted "${row.value}".`); refresh() },
    onError: e => { setConfirmDelete(null); show('err', errText(e)) },
  })

  function cancelAdd() { setAdding(false); setName(''); setKinds({ contact: true, firm: false }) }
  function submitAdd(e) {
    e.preventDefault()
    if (!name.trim()) return show('err', 'Enter a role name.')
    if (!chosen.length) return show('err', 'Choose individuals, firms, or both.')
    add.mutate()
  }

  const KIND_TAG = { contact: { label: 'Individual', cls: 'badge-blue' }, firm: { label: 'Firm', cls: 'badge-purple' } }
  const linkBtn = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <h2 style={{ fontSize: 15, fontWeight: 600 }}>Roles</h2>
        {!adding && (
          <button className="btn btn-primary btn-sm" onClick={() => setAdding(true)}>+ Add role</button>
        )}
      </div>

      {flash && (
        <div role={flash.kind === 'err' ? 'alert' : 'status'} style={{
          marginBottom: 12, padding: '8px 12px', borderRadius: 6, fontSize: 12,
          background: flash.kind === 'err' ? 'var(--danger-muted)' : 'var(--success-muted)',
          color: flash.kind === 'err' ? 'var(--danger-text, #f87171)' : 'var(--success-text, #34d399)',
        }}>{flash.text}</div>
      )}

      {adding && (
        <form onSubmit={submitAdd} style={{
          border: '1px solid var(--border-light)', borderRadius: 8, padding: 14, marginBottom: 14,
          background: 'var(--surface-raised)', display: 'flex', flexDirection: 'column', gap: 12,
        }}>
          <input className="input" autoFocus placeholder="Role name, e.g. Placement Agent"
            value={name} onChange={e => setName(e.target.value)} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', marginRight: 2 }}>Applies to</span>
            <RoleKindToggle on={kinds.contact} onClick={() => setKinds(k => ({ ...k, contact: !k.contact }))}>Individuals</RoleKindToggle>
            <RoleKindToggle on={kinds.firm} onClick={() => setKinds(k => ({ ...k, firm: !k.firm }))}>Firms</RoleKindToggle>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {chosen.length === 2 ? 'Both' : chosen.length === 0 ? 'Pick at least one' : ''}
            </span>
            <span style={{ flex: 1 }} />
            <button type="button" className="btn btn-secondary btn-sm" onClick={cancelAdd}>Cancel</button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={add.isPending || !name.trim() || !chosen.length}>
              {add.isPending ? 'Adding…' : 'Add role'}
            </button>
          </div>
        </form>
      )}

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}><div className="spinner" /></div>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 6 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '7px 12px',
            background: 'var(--surface-raised)', borderBottom: '1px solid var(--border)',
            fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)',
          }}>
            <span style={{ flex: 1 }}>Role</span>
            <span style={{ width: 70, textAlign: 'right' }}>In use</span>
          </div>
          {rows.map((r, i) => {
            const key = `${r.kind}:${r.value}`
            const system = r.standard && !r.in_forms
            const legacy = !r.standard
            const deletable = r.count === 0 && !system && !legacy
            const tag = KIND_TAG[r.kind]
            const groupStart = i > 0 && rows[i - 1].kind !== r.kind
            return (
              <div key={key} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', fontSize: 13,
                borderTop: groupStart ? '1px solid var(--border-light)' : undefined,
                borderBottom: i < rows.length - 1 && !(rows[i + 1].kind !== r.kind) ? '1px solid var(--border)' : 'none',
              }}>
                <span style={{ color: r.count ? 'var(--text-primary)' : 'var(--text-secondary)', fontWeight: r.count ? 500 : 400 }}>
                  {r.value}
                </span>
                <span className={`badge ${tag.cls}`} style={{ fontSize: 9 }}>{tag.label}</span>
                {system && <span className="badge badge-gray" style={{ fontSize: 9 }}>System</span>}
                {legacy && <span className="badge badge-orange" style={{ fontSize: 9 }}>Legacy</span>}
                <span style={{ flex: 1 }} />
                {deletable && (confirmDelete === key ? (
                  <span style={{ display: 'inline-flex', gap: 8 }}>
                    <button style={{ ...linkBtn, color: 'var(--danger)', fontWeight: 600 }} disabled={remove.isPending}
                      onClick={() => remove.mutate(r)}>Delete</button>
                    <button style={{ ...linkBtn, color: 'var(--text-muted)' }} onClick={() => setConfirmDelete(null)}>Cancel</button>
                  </span>
                ) : (
                  <button style={{ ...linkBtn, color: 'var(--text-muted)' }} onClick={() => setConfirmDelete(key)}>Delete</button>
                ))}
                <span style={{ width: 70, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                  color: r.count ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                  {r.count.toLocaleString()}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default function Settings() {
  const isPreview = useIsPreview()
  const { profile } = useAuth()
  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-text">
          <h1>Settings</h1>
          {!isPreview && <p>Manage integrations and account preferences.</p>}
        </div>
      </div>

      <div style={{ maxWidth: 700 }}>
        {/* Account section */}
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.08em', color: 'var(--text-secondary)', marginBottom: 12 }}>
            Account
          </h2>
          <AccountSection />
          {profile?.role === 'rga_admin' && <TeamAccessSection />}
          {profile?.role === 'rga_admin' && <RolesSection />}
        </div>

        {/* Integrations section */}
        <div style={{ marginBottom: 8 }}>
          <h2 style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.08em', color: 'var(--text-secondary)', marginBottom: 12 }}>
            Integrations
          </h2>
          <GmailSection />
          <CalendarSection />
          <MandateLabelMappingsSection />
          <UnmatchedEmailsSection />
        </div>
      </div>
    </div>
  )
}
